import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, finalize, map, mergeMap, of, switchMap, tap } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';

import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { AppService } from 'src/app/app.service';
import { getUsers, getUsersSuccess, getUserById, getUserByIdSuccess, createNewUser, createNewUserSuccess, updateUser, updateUserSuccess, deleteUser, deleteUserSuccess, usersError, approveUser, approveUserSuccess, performAction, performActionSuccess, getRoles, getRolesSuccess, getRosters, getRostersSuccess } from './users.action';
import { User } from 'src/app/models/users/user.model';
import { UsersService } from '../users.service';
import { DropdownValues } from './users.state';

@Injectable()
export class UsersEffects {
  getUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getUsers),
      switchMap((action) =>
        this.usersService.getUsers(action.page, action.search, action.sortBy, action.order, action.limit).pipe(
          map((data: any) => {
            return getUsersSuccess({ users: data });
          }),
          catchError((error: HttpErrorResponse) => {
            return of(usersError({ errorMessage: error.error.message || 'Something went wrong, please try again later!' }));
          }),
        ),
      ),
    ),
  );

  getUserById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getUserById),
      switchMap((action) =>
        this.usersService.getUserById(action.userId).pipe(
          map((user: User) => {
            return getUserByIdSuccess({ user });
          }),
          catchError((error: HttpErrorResponse) => {
            return of(usersError({ errorMessage: error.error.message || 'Something went wrong, please try again later!' }));
          }),
        ),
      ),
    ),
  );

  approveUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(approveUser),
      switchMap((action) =>
        this.usersService.approveUser(action.userId).pipe(
          map((user: User) => {
            this.toastr.success('User approved.', '', {
              positionClass: 'toast-top-center',
              closeButton: false,
            });
            return approveUserSuccess({ userId: action.userId });
          }),
          catchError((error: HttpErrorResponse) => {
            return of(usersError({ errorMessage: error.error.message || 'Something went wrong, please try again later!' }));
          }),
        ),
      ),
    ),
  );

  getRoles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getRoles),
      switchMap(() =>
        this.usersService.getRoles().pipe(
          map((roles: DropdownValues[]) => {
            return getRolesSuccess({ roles });
          }),
          catchError((error: HttpErrorResponse) => {
            return of(usersError({ errorMessage: error.error.message || 'Something went wrong, please try again later!' }));
          }),
        ),
      ),
    ),
  );

  getRosters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getRosters),
      switchMap(() =>
        this.usersService.getRosters().pipe(
          map((rosters: DropdownValues[]) => {
            return getRostersSuccess({ rosters });
          }),
          catchError((error: HttpErrorResponse) => {
            return of(usersError({ errorMessage: error.error.message || 'Something went wrong, please try again later!' }));
          }),
        ),
      ),
    ),
  );

  createNewUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createNewUser),
      switchMap((action) =>
        this.usersService.createNewUser(action.newUser).pipe(
          // Any 2xx is success. responseType is 'text', so the body is never
          // parsed or inspected - reaching here at all means the write landed.
          map(() => {
            return createNewUserSuccess();
          }),
          // Scoped to the inner pipe so a failure completes THIS request only
          // and leaves the outer actions$ stream alive for the next save.
          catchError((error: HttpErrorResponse) => {
            return of(usersError({ errorMessage: extractErrorMessage(error) }));
          }),
          // Runs on success AND failure, so the global loader (and therefore the
          // Save button) can never be left stuck.
          finalize(() => this.loaderService.hide()),
        ),
      ),
    ),
  );

  createNewUserSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(createNewUserSuccess),
        tap(() => {
          this.toastr.success('User created successfully', '', {
            positionClass: 'toast-top-center',
            closeButton: false,
          });
          this.router.navigate(['/dashboard/users']);
        }),
      ),
    { dispatch: false },
  );

  updateUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateUser),
      mergeMap((action) =>
        this.usersService.updateUser(action.updatedUser).pipe(
          // Any 2xx is success. responseType is 'text', so the body is never
          // parsed or inspected - reaching here at all means the write landed.
          map(() => {
            return updateUserSuccess();
          }),
          // Scoped to the inner pipe so a failure completes THIS request only
          // and leaves the outer actions$ stream alive for the next save.
          catchError((error: HttpErrorResponse) => {
            return of(usersError({ errorMessage: extractErrorMessage(error) }));
          }),
          // Runs on success AND failure, so the global loader (and therefore the
          // Save button) can never be left stuck.
          finalize(() => this.loaderService.hide()),
        ),
      ),
    ),
  );

  updateUserSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(updateUserSuccess),
        tap(() => {
          this.toastr.success('User updated', '', {
            positionClass: 'toast-top-center',
            closeButton: false,
          });
          this.router.navigate(['/dashboard/users']);
        }),
      ),
    { dispatch: false },
  );

  // usersError was dispatched from every catchError in this file but nothing
  // ever handled it - the reducer stored errorMessage and no view read it, so
  // failures were completely silent. One handler covers create, update and all
  // other users actions, since they all funnel into usersError.
  usersError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(usersError),
        tap(({ errorMessage }) => {
          this.toastr.error(errorMessage, '', {
            positionClass: 'toast-top-center',
            closeButton: false,
          });
        }),
      ),
    { dispatch: false },
  );

  deleteUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteUser),
      mergeMap((action) =>
        this.usersService.deleteUser(action.userId).pipe(
          map(() => {
            this.toastr.success('User successfully deleted', '', {
              positionClass: 'toast-top-center',
              closeButton: false,
            });
            return deleteUserSuccess({ userId: action.userId });
          }),
          catchError((error) => {
            return of(usersError({ errorMessage: error.error.message || 'Something went wrong, please try again later!' }));
          }),
        ),
      ),
    ),
  );

  performAction$ = createEffect(() =>
    this.actions$.pipe(
      ofType(performAction),
      mergeMap((action) =>
        this.usersService.performAction(action.actionOnUsers).pipe(
          map(() => {
            this.toastr.success(`User successfully ${action.actionOnUsers.action}d`, '', {
              positionClass: 'toast-top-center',
              closeButton: false,
            });
            return performActionSuccess({ actionOnUsers: action.actionOnUsers });
          }),
          catchError((error) => {
            return of(usersError({ errorMessage: error.error.message || 'Something went wrong, please try again later!' }));
          }),
        ),
      ),
    ),
  );

  constructor(
    private actions$: Actions,
    private usersService: UsersService,
    private toastr: ToastrService,
    private router: Router,
    private loaderService: AppService,
  ) { }
}

/**
 * The API returns errors in several shapes (JSON body, plain-text body from
 * responseType:'text', or no body at all when the request never reached the
 * server). The previous `error.error.message` threw its own TypeError on the
 * last two, which swallowed the real failure. Optional chaining throughout,
 * matching signin.service.ts.
 */
function extractErrorMessage(error: HttpErrorResponse): string {
  return (
    error?.error?.message ??
    error?.error?.error?.message ??
    (typeof error?.error === 'string' && error.error.trim() ? error.error : null) ??
    error?.message ??
    'Something went wrong, please try again later!'
  );
}
